import { Component, OnInit, Input, ViewChild, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { StorageType, SoftwareOptionsForCompany } from '../enums';
import { DownloadLinkModal } from '@shared/downloadLinkModal/downloadLinkModal.component';
import { TranslateService } from '@ngx-translate/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { IteroLabService } from '@shared/iteroLab.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { WindowsApplications } from './WindowsApplications';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BaseDestroyable } from '@core/base-destroyable';
import { ShellContextService } from '../../services/shell-context/shell-context.service';
import { Order } from '@shared/orders/order';
import { OrdersService } from '@shared/orders/orders.service';

@Component({
	selector: 'windows-app-button',
	templateUrl: './windowsAppButton.component.html',
	styleUrls: ['./windowsAppButton.component.scss'],
	providers: [IteroLabService],
})
export class WindowsAppButtonComponent extends BaseDestroyable implements OnInit, OnChanges, OnDestroy {
	// Mandatory
	@Input() public application: string;
	@Input() public order: Order;
	@Input() id: string;

	// Non-mandatory
	@Input() public title: string;
	@Input() public user = 'doctor';
	@Input() public windowsOnly = true;
	@Input() public modalAllowed = true;

	@ViewChild(DownloadLinkModal) downloadLinkModal: DownloadLinkModal;

	@Output() openModal: EventEmitter<any> = new EventEmitter();

	public orthoCadSetupDownloadUrl: string;
	public iTeroSetupDownloadUrl: string;
	public orthoCadSetupDownloadUrl_acs: string;
	public iTeroSetupDownloadUrl_acs: string;

	disclaimer = 'Orders.Disclaimer';
	isProgressAssesmentEnabled = false;
	isCaseAssesmentEnabled = false;
	isOrthoCadHidden = false;
	modalFooter = false;
	isOrthoModal = false;

	constructor(
		private eupRoutesService: EupRoutesService,
		private orderService: OrdersService,
		private translateService: TranslateService,
		public globalSettingsService: GlobalSettingsService,
		private shellContextService: ShellContextService,
		private iteroLabService: IteroLabService,
		private softwareOptionsService: SoftwareOptionsService
	) {
		super();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.application.currentValue !== changes.application.previousValue) {
			this.disclaimer = this.nameMap[changes.application.currentValue] === 'Orders.OpenWithOrthoCAD' ? 'Orders.Disclaimer' : '';
		}
	}

	ngOnInit(): void {
		this.orthoCadSetupDownloadUrl = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl;
		this.iTeroSetupDownloadUrl = this.eupRoutesService.downloads.iTeroSetupDownloadUrl;
		this.orthoCadSetupDownloadUrl_acs = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl_acs;
		this.iTeroSetupDownloadUrl_acs = this.eupRoutesService.downloads.iTeroSetupDownloadUrl_acs;

		this.shellContextService.selectedCompanyId$
			.pipe(
				takeUntil(this.componentAlive$),
				switchMap(companyId => this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId)),
				map(softwareOptions => Object.values(softwareOptions))
			)
			.subscribe(softwareOptions => {
				this.isProgressAssesmentEnabled = softwareOptions.includes(SoftwareOptionsForCompany.ProgressAssesment);
				this.isCaseAssesmentEnabled = softwareOptions.includes(SoftwareOptionsForCompany.CaseAssesment);
				this.isOrthoCadHidden = softwareOptions.includes(SoftwareOptionsForCompany.cswo_Hide_OrthoCAD_Show_3DM);
			});

		if (!this.title) {
			this.title = this.translateService.instant(this.nameMap[this.application]);
		}
		if (this.application === WindowsApplications.Orthocad) {
			this.isOrthoModal = true;
			this.modalFooter = false;
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	public get available(): boolean {
		return this.visibilityMap[this.user][this.application];
	}

	public onClick(): void {
		this.orderService
			.getFileStorage(this.order.id)
			.pipe(
				take(1),
				tap((storage: StorageType) => this.onclickHandler(storage))
			)
			.subscribe();
	}

	private onclickHandler(storage: StorageType) {
		const appProtocolLink = this.linkMap[this.application];
		const downloadLink = this.installationLinksMap[this.application][storage];
		const name = this.nameMap[this.application];
		const title = this.translateService.instant(name);

		if (this.iteroLabService.isNeedToSendToLab(this.order)) {
			this.openLabApplication(title, downloadLink);
		} else {
			this.openApp(title, downloadLink, appProtocolLink);
		}
	}

	private openLabApplication(title, downloadLink) {
		this.iteroLabService
			.prepareOpenApp(this.order.id, this.order.lastDetailsId)
			.pipe(
				take(1),
				tap(url => {
					this.openApp(title, downloadLink, url);
					this.orderService.updateLabOrderById(
						this.order.id.toString(),
						this.globalSettingsService.get().selectedDoctorId.toString(),
						this.globalSettingsService.get().selectedCompanyId.toString(),
						(order: Order) => {
							// optimistic UI issue
							order.canReturnToDoctor = false;
						}
					);
				})
			)
			.subscribe();
	}

	private openApp(title: string, downloadLink: string, appProtocolLink: string): void {
		this.orderService.startProtocol(appProtocolLink, true);
		if (this.modalAllowed) {
			this.downloadLinkModal.show(title, downloadLink);
		} else {
			this.openModal.emit({ title, downloadLink });
		}
	}

	public get customWidth() {
		return this.widthMap[this.application];
	}

	private get visibilityMap() {
		return {
			lab: {
				[WindowsApplications.Orthocad]: this.order.canOpenWithOrthoCad && !this.isOrthoCadHidden,
			},
		};
	}

	private get linkMap() {
		return {
			[WindowsApplications.Orthocad]: this.order.openFileLink,
		};
	}

	private get nameMap() {
		return {
			[WindowsApplications.Orthocad]: 'Orders.OpenWithOrthoCAD',
		};
	}

	private get installationLinksMap() {
		return {
			[WindowsApplications.Orthocad]: {
				[StorageType.ACS]: this.orthoCadSetupDownloadUrl_acs,
				[StorageType.OriginalFMS]: this.orthoCadSetupDownloadUrl,
			},
		};
	}

	private get widthMap() {
		return {
			[WindowsApplications.Orthocad]: undefined,
		};
	}
}
